import { type MartPresetMeta, type MartPreset } from "modules/datatable/types"
import type { SubscriptionStatusType, UserType } from "./Types"
import type { ColumnVO } from "@tokenterminal/core/feature/ag-grid-datatable/index"
import type { POSSIBLE_METRICS_ENUM } from "constants/metrics"

export type ProjectMetricAggregateIntervals =
  | "1d" // 24h
  | "7d"
  | "30d"
  | "90d"
  | "180d"
  | "365d"
  | "all"
  | "ytd"
  | "4fullq"

type AggregateValue = number | null

type ProjectAggregateNumbers = {
  [key in Exclude<
    ProjectMetricAggregateIntervals,
    ["all", "ytd"]
  >]?: AggregateValue
}

type ProjectAggregateNumbersWithAll = {
  [key in ProjectMetricAggregateIntervals]?: AggregateValue
}

type ProjectAggregateAnnualized = {
  "30d"?: AggregateValue
}

type ProjectMetricAggregatesForValues = {
  latest?: AggregateValue
  max?: AggregateValue
  ath?: AggregateValue
  atl?: AggregateValue
}

type ProjectMetricEntry = {
  averages?: ProjectAggregateNumbers // Default zooms
  changes?: ProjectAggregateNumbers // Default zooms
  trends?: ProjectAggregateNumbers // Default zooms
  sums?: ProjectAggregateNumbersWithAll // Default zoom + all
  values?: ProjectMetricAggregatesForValues
  moving_average_annualized_trend?: ProjectAggregateAnnualized // 30d zoom
  moving_average_annualized?: ProjectAggregateAnnualized // 30d zoom
}

export type ProjectMetricAggregationKeys = keyof ProjectMetricEntry

// New GMV split metrics are by project, so they will unfortunately simple not exist as keys
type ProjectMetricAggregations = {
  [key in Exclude<
    POSSIBLE_METRICS_ENUM,
    "vesting_schedule"
  >]?: ProjectMetricEntry
} & {
  vesting_schedule?: never
}

type ProjectMetricAvailability = {
  [key in POSSIBLE_METRICS_ENUM]?: boolean
} & {
  deposits: boolean // Probably to be used in near future. also it exists already

  vesting_schedule: never
  composition: Record<string, boolean> | false
  chain: Record<string, boolean> | false
}

export enum ProjectSocialChannelNameEnum {
  github = "github",
  twitter = "twitter",
  discord = "discord",
  etherscan = "etherscan",
  website = "website",
  governance = "governance",
}

export type MarketSector = {
  id: string
  name: string
  metric_id: string | null
  description: string
  description_what: string
  description_how: string
  tier: SubscriptionStatusType
  market_sector_metric_availability: ProjectMetricAvailability
}

export type ProjectType = {
  chains: string[]
  business_model: {
    demand_side_label?: string
    expense_label?: string
    expense_side_label?: string
    expense_value?: string
    fees_label?: string
    fees_value?: string
    protocol_label?: string
    revenue_label?: string
    revenue_value?: string
    supply_side_fees_label?: string
    supply_side_fees_value?: string
    supply_side_label?: string
  }
  description_how: string
  description_what: string
  description_who: string
  exchanges_token_listed_on: string[]
  is_new: boolean
  links?: { title: ProjectSocialChannelNameEnum; url: string }[]
  logo: string
  market_sectors: Array<MarketSector>
  market_sector_id: string
  metric_availability: ProjectMetricAvailability
  metric_aggregations?: Partial<ProjectMetricAggregations> // undefined possibility because of a Preview Project
  metrics_by_aggregates: Record<string, Record<POSSIBLE_METRICS_ENUM, boolean>>
  metric_definitions: {
    [key: string]: string
  }
  metric_sources: {
    [key: string]: string
  }
  metric_tooltips?: {
    [key: string]: string
  }
  name: string
  slug: string
  notion_one_pager_page_id?: string
  notion_business_model_page_id?: string
  project_id: string
  project_logo: string
  dbt_tag: string | null
  smart_contract_registry: string | null
  symbol: string
  status_label: string
  bb_ticker?: string
}

export type AuthType = { token: string }

export type UserDataType = { user: UserType }

/**
 * Marts
 */

export type MartId = string // Dynamic now, so can't be strictly typed

export type MartFetcherOptions = {
  limit?: number
  offset?: number
  rowGroupCols?: ColumnVO[]
  groupKeys?: string[]
}

// Fetcher body defaults to per mart
export type MartFetcher<T extends Array<Record<string, unknown>> = []> = (
  body: undefined,
  options: MartFetcherOptions
) => Promise<T>

export interface MartColumn {
  column_index: number
  enabled: string[]
  col_id: string
  field: string
  header_name: string
  header_tooltip: string | null
  parent: string | null
  cell_data_type: string
  enable_row_group: boolean
  enable_value_row: boolean
  sortable: boolean
  created_at: Date
  updated_at: Date
}

interface MartMeta {
  mart_id: string
  name: string
  slug: string
  description: string | null
  presets: Array<MartPresetMeta>
}

export interface Mart extends MartMeta {
  created_at: Date
  updated_at: Date
  column_definitions: Array<MartColumn>
  default_preset: MartPreset
}

/**
 * Chain Status
 */
export interface ChainStatusItem {
  slug: string
  api_id: string
  last_approved_timestamp: string
  is_fresh: boolean
}

export interface ChainStatusResponse {
  data: ChainStatusItem[]
}
